<script setup lang="ts">
import { type FormGroupProps, useFormGroup, useFormContext, HiddenField } from "@formwerk/core";
import type { CustomerDetails } from "@/schemas/customer";

const props = defineProps<FormGroupProps & { prefix: "billing" | "shipping" }>();
const { labelProps, groupProps } = useFormGroup(props);
const { values, setValue } = useFormContext();
const countryOptions = computed(() => Object.entries(countries).map(([code, country]) => ({ label: country.name, value: code })));
const stateOptions = computed(() => {
  const country = (values[props.name] as CustomerDetails | undefined)?.country || "CA";
  const states = countries[country]?.states || {};
  return Object.entries(states).map(([code, label]) => ({ label, value: code }));
});
watch(() => (values[props.name] as Address | undefined)?.country, (newCountry) => {
  const newStates = Object.keys(countries[newCountry || ""]?.states || {});
  setValue<string>(`${props.name}.state`, newStates[0] || "");
});
</script>

<template>
  <fieldset v-bind="groupProps" class="@container">
    <legend v-if="label" v-bind="labelProps">
      {{ label }}
    </legend>
    <div class="grid gap-4">
      <HiddenField name="prefix" :value="prefix" />
      <FieldText name="first_name" type="text" label="Prénom" required />
      <FieldText name="last_name" type="text" label="Nom" required />
      <FieldText name="company" type="text" label="Nom de l'entreprise" class="@2xl:col-span-2" />
      <FieldText v-if="prefix === 'billing'" name="email" type="email" label="Courriel" required />
      <HiddenField v-else name="email" :value="values.email || '...'" />
      <FieldText name="phone" type="tel" label="Téléphone" required :class="{ '@2xl:col-span-2': prefix === 'shipping' }" />
      <FieldText name="address_1" type="text" label="Adresse" required class="@2xl:col-span-2" />
      <FieldText name="address_2" type="text" label="Adresse (ligne 2)" class="@2xl:col-span-2" />
      <FieldText name="city" type="text" label="Ville" required />
      <FieldText name="postcode" type="text" label="Code postal" required />
      <FieldSelect name="state" :options="stateOptions" label="Province / État" required />
      <FieldSelect name="country" :options="countryOptions" label="Pays" required />
    </div>
  </fieldset>
</template>
